import { DIMENSIONS } from "./constants";

/*
  The URL is the persisted source of truth for the selection. Compact encoding: short dimension keys,
  comma-joined values, ids/keys (never display names), nothing for an empty selection.

    ?li=12,14&co=acme-ltd&fn=IT

  Built by hand rather than with URLSearchParams: each value is encoded BEFORE joining, so a value containing a
  comma can never be mistaken for two values, and nothing is double-encoded.
*/

export const FILTER_URL_KEYS = {
  lineItemId: "li",
  companyKey: "co",
  assetKey: "as",
  jobFunction: "fn",
  seniority: "sn",
  industry: "in",
  country: "ct"
};

export const MAX_URL_LENGTH = 1500;

const DIMENSION_BY_URL_KEY = Object.keys(FILTER_URL_KEYS).reduce((acc, dim) => {
  acc[FILTER_URL_KEYS[dim]] = dim;
  return acc;
}, {});

/**
 * @returns {{ search: string, truncated: boolean }} `search` is "" or starts with "?". When the selection would
 * exceed `maxLength`, values are dropped from the end (deterministically) and `truncated` is true, so the page
 * can say so instead of the URL breaking.
 */
export function serializeFilters(filters, maxLength = MAX_URL_LENGTH) {
  const kept = {};
  let truncated = false;

  const render = () => {
    const parts = DIMENSIONS.filter((d) => kept[d] && kept[d].length).map((d) => `${FILTER_URL_KEYS[d]}=${kept[d].join(",")}`);
    return parts.length ? `?${parts.join("&")}` : "";
  };

  // Values go in dimension order, sorted within a dimension, so the same selection always produces the same URL and
  // a too-long one is cut at the same place every time (everything after the first value that does not fit is dropped).
  DIMENSIONS.forEach((dimension) => {
    const values = filters[dimension];
    if (truncated || !values || !values.size) { return; }

    [...values].sort().forEach((value) => {
      if (truncated) { return; }

      kept[dimension] = [...(kept[dimension] || []), encodeURIComponent(value)];

      if (render().length > maxLength) {
        kept[dimension].pop();
        truncated = true;
      }
    });
  });

  return { search: render(), truncated };
}

/**
 * Parses a location.search and validates it against `knownValues` (`{ [dimension]: Set }`, from the payload).
 * Unknown dimensions are ignored; unknown or malformed values are dropped and counted in `dropped`, so a stale or
 * hand-edited link can never filter the page down to nothing with no way back.
 *
 * @returns {{ filters: object, dropped: number }}
 */
export function parseFilters(search, knownValues) {
  const filters = {};
  let dropped = 0;

  const query = (search || "").replace(/^\?/, "");
  if (!query) { return { filters, dropped }; }

  query.split("&").forEach((pair) => {
    const eq = pair.indexOf("=");
    if (eq < 1) { return; }

    const dimension = DIMENSION_BY_URL_KEY[pair.slice(0, eq)];
    if (!dimension) { return; } // not ours (or a typo); ignore without a notice

    const values = new Set();
    pair.slice(eq + 1).split(",").forEach((raw) => {
      if (raw === "") { return; }

      let value = null;
      // A malformed escape throws; drop that value, never take the page down.
      try { value = decodeURIComponent(raw); } catch (e) { value = null; }

      if (value !== null && value !== "" && knownValues && knownValues[dimension] && knownValues[dimension].has(value)) {
        values.add(value);
      } else {
        dropped += 1;
      }
    });

    if (values.size) {
      const merged = new Set([...(filters[dimension] || []), ...values]);
      filters[dimension] = merged;
    }
  });

  return { filters, dropped };
}
